import React from 'react';
import { IconButton, CircularProgress, Tooltip, } from '@mui/material';

interface BaseBadgeProps {
  icon: React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
  size?: 'small' | 'medium';
  tooltipTitle: string;
  statusColor: string;
  isActive?: boolean;
  activeBackgroundColor?: string;
  activeHoverColor?: string;
}

const BaseBadge: React.FC<BaseBadgeProps> = ({ 
  icon, 
  loading, 
  onClick, 
  size = 'small',
  tooltipTitle,
  statusColor,
  isActive = false,
  activeBackgroundColor = 'action.hover',
  activeHoverColor = 'success.dark'
}) => {
  const iconSize = size === 'small' 
    ? { xs: 18, sm: 20, md: 22, lg: 24 } 
    : { xs: 20, sm: 22, md: 24, lg: 26 };

  return (
    <Tooltip 
      title={tooltipTitle} 
      enterDelay={300}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.black',
            fontSize: '0.8125rem',
            fontWeight: 500,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          },
        },
        arrow: {
          sx: {
            color: 'background.black',
          },
        },
      }}
    >
      <IconButton
        size={size}
        onClick={onClick}
        sx={{
          color: loading ? 'text.grey' : statusColor,
          padding: { xs: '6px', sm: '8px', md: '10px' },
          borderRadius: 2,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: isActive ? activeBackgroundColor : 'transparent',
          '&:hover': {
            backgroundColor: 'action.hover',
            color: isActive ? activeHoverColor : statusColor,
            transform: 'scale(1.1)',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        {loading ? (
          <CircularProgress size={16} sx={{ color: 'inherit' }} />
        ) : (
          React.cloneElement(icon as React.ReactElement, { 
            sx: { fontSize: iconSize } 
          })
        )}
      </IconButton>
    </Tooltip>
  );
};

export default BaseBadge;
